import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { CheckSquare, Clock, AlertTriangle, Plus, Filter, X, User as UserIcon, Zap } from 'lucide-react';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  STAFF TASKS PAGE
//  Assign / track tasks. Can be auto-created by SOP trigger service.
// ═══════════════════════════════════════════════════════════════════

const PRIORITY_STYLE = {
  urgent: { bg: '#fef2f2', text: '#b91c1c', accent: '#dc2626', label: '🔥 Urgent' },
  high:   { bg: '#fff7ed', text: '#c2410c', accent: '#ea580c', label: '⬆️ High' },
  normal: { bg: '#eff6ff', text: '#1e40af', accent: '#3b82f6', label: '➡️ Normal' },
  low:    { bg: '#f0fdf4', text: '#15803d', accent: '#22c55e', label: '⬇️ Low' },
};

const STATUS_STYLE = {
  open:         { bg: '#f3f4f6', text: '#4b5563', label: 'Open' },
  'in-progress':{ bg: '#dbeafe', text: '#1e40af', label: 'In Progress' },
  completed:    { bg: '#d1fae5', text: '#065f46', label: 'Completed' },
  overdue:      { bg: '#fee2e2', text: '#991b1b', label: 'Overdue' },
  skipped:      { bg: '#f3f4f6', text: '#6b7280', label: 'Skipped' },
};

const CATEGORY_ICONS = {
  clinical: '🩺', sterilization: '♨️', admin: '📋', inventory: '📦',
  compliance: '🛡️', marketing: '📣', housekeeping: '🧹', training: '🎓', other: '📌',
};

const BLANK_FORM = {
  title: '', description: '', priority: 'normal', category: 'other',
  dueDate: '', dueTime: '', assignedToName: '', role: '',
};

const StaffTasksPage = ({ API_BASE, currentUser, users = [] }) => {
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState({ byStatus: {}, byPriority: {}, overdue: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: 'open', priority: '', assignee: '', q: '' });
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const isAdmin = ['owner','admin','super_admin'].includes(currentUser?.role);
  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status)   params.status = filter.status;
      if (filter.priority) params.priority = filter.priority;
      if (filter.assignee) params.assigneeName = filter.assignee;
      if (filter.q)        params.q = filter.q;
      const [list, sum] = await Promise.all([
        axios.get(`${API_BASE}/staff-tasks`, { params }),
        axios.get(`${API_BASE}/staff-tasks/summary`),
      ]);
      setTasks(list.data || []);
      setSummary(sum.data || { byStatus: {}, byPriority: {}, overdue: 0 });
    } catch (err) { showMsg('error', err.response?.data?.error || err.message); }
    finally { setLoading(false); }
  }, [API_BASE, filter]);

  useEffect(() => { load(); }, [load]);

  const openForm = (task = null) => {
    if (task) {
      setEditingTask(task);
      setForm({
        title: task.title, description: task.description || '',
        priority: task.priority, category: task.category,
        dueDate: task.dueDate ? task.dueDate.slice(0, 10) : '',
        dueTime: task.dueTime || '',
        assignedToName: task.assignedToName || '',
        role: task.role || '',
      });
    } else {
      setEditingTask(null);
      setForm(BLANK_FORM);
    }
    setShowForm(true);
  };

  const saveTask = async () => {
    if (!form.title) return showMsg('error', 'Title required');
    try {
      const body = {
        ...form,
        assignedBy: currentUser?.fullName || currentUser?.username,
        dueDate: form.dueDate ? new Date(form.dueDate) : null,
      };
      if (editingTask) await axios.put(`${API_BASE}/staff-tasks/${editingTask._id}`, body);
      else             await axios.post(`${API_BASE}/staff-tasks`, body);
      showMsg('success', editingTask ? 'Updated' : 'Task created');
      setShowForm(false);
      load();
    } catch (err) { showMsg('error', err.response?.data?.error || err.message); }
  };

  const completeTask = async (id) => {
    try {
      await axios.post(`${API_BASE}/staff-tasks/${id}/complete`, {
        completedBy: currentUser?.fullName || currentUser?.username,
      });
      showMsg('success', '✓ Completed');
      load();
    } catch (err) { showMsg('error', err.response?.data?.error || err.message); }
  };

  const claimTask = async (id) => {
    try {
      await axios.post(`${API_BASE}/staff-tasks/${id}/claim`, {
        userId: currentUser?._id || currentUser?.id,
        userName: currentUser?.fullName || currentUser?.username,
      });
      load();
    } catch (err) { showMsg('error', err.response?.data?.error || err.message); }
  };

  const deleteTask = async (id) => {
    if (!window.confirm('Task delete karein?')) return;
    try {
      await axios.delete(`${API_BASE}/staff-tasks/${id}`);
      load();
    } catch (err) { showMsg('error', err.response?.data?.error || err.message); }
  };

  const statCards = useMemo(() => [
    { label: 'Open',        val: summary.byStatus.open || 0,           color: theme.info,    icon: '📋' },
    { label: 'In Progress', val: summary.byStatus['in-progress'] || 0, color: theme.purple,  icon: '⚙️' },
    { label: 'Overdue',     val: summary.overdue,                       color: theme.danger,  icon: '⚠️' },
    { label: 'Completed',   val: summary.byStatus.completed || 0,      color: theme.success, icon: '✅' },
  ], [summary]);

  return (
    <div>
      {/* HEADER */}
      <div style={{ ...cardStyle, marginBottom: 14, background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`, color: 'white', border: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <CheckSquare size={32} />
          <div style={{ flex: 1, minWidth: 180 }}>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.35rem' }}>Staff Tasks</h2>
            <p style={{ margin: '2px 0 0', opacity: 0.9, fontSize: 13 }}>Assign, track, and complete team tasks — auto-triggered ones from SOP workflow bhi yahan dikhte hain</p>
          </div>
          <button onClick={() => openForm()} style={{ ...btnStyle, marginTop: 0, backgroundColor: 'white', color: theme.primaryDark }}>
            <Plus size={16} /> <span style={{ marginLeft: 6 }}>New Task</span>
          </button>
        </div>
      </div>

      {/* STATS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
        {statCards.map(s => (
          <div key={s.label} style={{ ...cardStyle, padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 22 }}>{s.icon}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.val}</div>
            </div>
            <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 4, fontWeight: 600 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {msg.text && (
        <div style={{
          padding: '10px 14px', marginBottom: 12, borderRadius: 8,
          background: msg.type === 'error' ? '#fee' : '#efe',
          color: msg.type === 'error' ? theme.danger : theme.success,
          border: `1px solid ${msg.type === 'error' ? theme.danger : theme.success}44`,
          fontWeight: 600, fontSize: 13.5,
        }}>{msg.text}</div>
      )}

      {/* FILTERS */}
      <div style={{ ...cardStyle, padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Filter size={16} color={theme.textMuted} />
        <select value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))} style={miniSelect}>
          <option value="">All status</option>
          <option value="open">Open</option>
          <option value="in-progress">In Progress</option>
          <option value="completed">Completed</option>
        </select>
        <select value={filter.priority} onChange={e => setFilter(f => ({ ...f, priority: e.target.value }))} style={miniSelect}>
          <option value="">Any priority</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="normal">Normal</option>
          <option value="low">Low</option>
        </select>
        <input placeholder="Assignee name…" value={filter.assignee} onChange={e => setFilter(f => ({ ...f, assignee: e.target.value }))} style={{ ...miniSelect, width: 140 }} />
        <input placeholder="Search title/description…" value={filter.q} onChange={e => setFilter(f => ({ ...f, q: e.target.value }))} style={{ ...miniSelect, flex: 1, minWidth: 150 }} />
      </div>

      {/* LIST */}
      {loading ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 40, color: theme.textMuted }}>Loading…</div>
      ) : tasks.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 50 }}>
          <CheckSquare size={40} color={theme.textSubtle} style={{ opacity: 0.5 }} />
          <p style={{ fontWeight: 600, color: theme.textMuted, marginTop: 10 }}>Koi task nahi</p>
          <p style={{ fontSize: 13, color: theme.textSubtle }}>"New Task" dabayein ya SOP trigger ho jaye to auto ban jayega</p>
        </div>
      ) : (
        tasks.map(t => {
          const p = PRIORITY_STYLE[t.priority] || PRIORITY_STYLE.normal;
          const s = STATUS_STYLE[t.status] || STATUS_STYLE.open;
          const overdue = t.dueDate && new Date(t.dueDate) < new Date() && (t.status === 'open' || t.status === 'in-progress');
          const isPending = t.status === 'open' || t.status === 'in-progress';
          return (
            <div key={t._id} style={{
              ...cardStyle, padding: 14, marginBottom: 8,
              borderLeft: `4px solid ${overdue ? theme.danger : p.accent}`,
            }}>
              <div style={{ display: 'flex', gap: 12 }}>
                <span style={{ fontSize: 22 }}>{CATEGORY_ICONS[t.category] || '📌'}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: theme.navy }}>{t.title}</span>
                    <span style={{ ...pill, background: p.bg, color: p.text }}>{p.label}</span>
                    <span style={{ ...pill, background: s.bg, color: s.text }}>{s.label}</span>
                    {overdue && <span style={{ ...pill, background: theme.danger, color: 'white' }}>OVERDUE</span>}
                    {t.autoTriggered && <span style={{ ...pill, background: theme.purple + '22', color: theme.purple }}><Zap size={10} /> AUTO</span>}
                  </div>
                  {t.description && <p style={{ margin: '4px 0', fontSize: 13, color: theme.textMuted, lineHeight: 1.5 }}>{t.description}</p>}
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 11.5, color: theme.textSubtle, marginTop: 3 }}>
                    {t.assignedToName ? <span>👤 {t.assignedToName}</span> : t.role ? <span>👥 Any {t.role}</span> : <span style={{ color: theme.warning, fontWeight: 600 }}>Unassigned</span>}
                    {t.patientName && <span>🧑‍⚕️ {t.patientName}</span>}
                    {t.dueDate && <span>📅 Due: {new Date(t.dueDate).toLocaleDateString('en-IN')}{t.dueTime ? ` ${t.dueTime}` : ''}</span>}
                    {t.assignedBy && <span>➜ by {t.assignedBy}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {!t.assignedTo && isPending && (
                    <button onClick={() => claimTask(t._id)} style={smallBtn(theme.info)}>
                      <UserIcon size={12} /> Claim
                    </button>
                  )}
                  {isPending && (
                    <button onClick={() => completeTask(t._id)} style={smallBtn(theme.success)}>✓ Complete</button>
                  )}
                  {isAdmin && (
                    <>
                      <button onClick={() => openForm(t)} style={smallBtn(theme.textMuted, true)}>Edit</button>
                      <button onClick={() => deleteTask(t._id)} style={smallBtn(theme.danger, true)}>✕</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })
      )}

      {/* FORM MODAL */}
      {showForm && (
        <div style={modalBg} onClick={() => setShowForm(false)}>
          <div style={modalBox} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h3 style={{ margin: 0, color: theme.navy }}>{editingTask ? 'Edit Task' : 'New Task'}</h3>
              <button onClick={() => setShowForm(false)} style={{ border: 'none', background: 'none', cursor: 'pointer' }}><X size={20} color={theme.textMuted} /></button>
            </div>
            <label style={lbl}>Title *</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={inputStyle} placeholder="Clean autoclave chamber" />
            <label style={lbl}>Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={{ ...inputStyle, minHeight: 60 }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={lbl}>Priority</label>
                <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} style={inputStyle}>
                  <option value="low">Low</option><option value="normal">Normal</option>
                  <option value="high">High</option><option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Category</label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={inputStyle}>
                  {Object.entries(CATEGORY_ICONS).map(([k, i]) => <option key={k} value={k}>{i} {k}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
              <div>
                <label style={lbl}>Due Date</label>
                <input type="date" value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={lbl}>Due Time</label>
                <input type="time" value={form.dueTime} onChange={e => setForm(f => ({ ...f, dueTime: e.target.value }))} style={inputStyle} />
              </div>
            </div>
            <label style={lbl}>Assign to (name)</label>
            <input value={form.assignedToName} onChange={e => setForm(f => ({ ...f, assignedToName: e.target.value }))} style={inputStyle} placeholder="Leave empty for 'any role'" list="users-list" />
            <datalist id="users-list">
              {users.map(u => <option key={u._id || u.username} value={u.fullName || u.username} />)}
            </datalist>
            <label style={lbl}>Or role (if not assigning to specific person)</label>
            <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} style={inputStyle}>
              <option value="">— none —</option>
              <option value="doctor">Doctor</option>
              <option value="nurse">Nurse</option>
              <option value="receptionist">Receptionist</option>
              <option value="lab">Lab technician</option>
              <option value="housekeeping">Housekeeping</option>
            </select>
            <button onClick={saveTask} style={{ ...btnStyle, width: '100%' }}>
              {editingTask ? 'Update Task' : 'Create Task'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── styles ───
const pill = { padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3 };
const miniSelect = { ...inputStyle, width: 'auto', padding: '6px 10px', fontSize: 13 };
const smallBtn = (color, outline = false) => ({
  padding: '5px 11px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
  border: outline ? `1px solid ${color}33` : 'none',
  background: outline ? color + '12' : color,
  color: outline ? color : 'white',
  display: 'inline-flex', alignItems: 'center', gap: 4,
});
const modalBg = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
const modalBox = { background: 'white', borderRadius: 12, padding: 20, width: '100%', maxWidth: 500, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' };
const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: theme.textMuted, margin: '10px 0 4px' };

export default StaffTasksPage;
